import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { useAuth } from "./AuthContext";
import LockUnlock from "./LockUnlock";
import { toast } from "sonner";

/**
 * Admin "Token ID" (2FA) dialog. Two phases:
 *   • Not enrolled → QR code + 6-digit input to confirm enrollment.
 *   • Enrolled     → 6-digit input to verify.
 * On success the lock unlocks (~1s) before the session is finalized.
 *
 * Preview: the code `000000` is accepted while on the preview host.
 */
export default function TwoFactorDialog() {
  const { pending2FA, beginEnrollment, verifyTwoFactor, finalizeAdmin, cancelTwoFactor } = useAuth();
  const [enrollment, setEnrollment] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [loadingQr, setLoadingQr] = useState(false);
  const [unlocked, setUnlocked] = useState(false);

  const isEnrollment = pending2FA && !pending2FA.enrolled;
  const open = Boolean(pending2FA);
  const tierLabel = pending2FA?.tier === "super" ? "Super Admin" : "Area Manager";

  useEffect(() => {
    let cancelled = false;
    let t;
    if (!isEnrollment) {
      t = setTimeout(() => { if (!cancelled) setEnrollment(null); }, 0);
      return () => { cancelled = true; clearTimeout(t); };
    }
    t = setTimeout(() => { if (!cancelled) setLoadingQr(true); }, 0);
    beginEnrollment()
      .then(data => { if (!cancelled) setEnrollment(data); })
      .catch(() => { if (!cancelled) toast.error("Could not start 2FA setup — sign in again"); })
      .finally(() => { if (!cancelled) setLoadingQr(false); });
    return () => { cancelled = true; clearTimeout(t); };
  }, [isEnrollment, beginEnrollment]);

  useEffect(() => {
    if (open) return undefined;
    const t = setTimeout(() => { setCode(""); setEnrollment(null); setBusy(false); setUnlocked(false); }, 0);
    return () => clearTimeout(t);
  }, [open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!/^[0-9]{6}$/.test(code)) { toast.error("Enter your 6-digit code"); return; }
    setBusy(true);
    const result = await verifyTwoFactor(code);
    if (!result.valid) {
      setBusy(false);
      toast.error(result.error || "Invalid code");
      setCode("");
      return;
    }
    // Play the unlock animation, then finalize the session.
    setUnlocked(true);
    setTimeout(() => {
      finalizeAdmin(result);
      toast.success(`${tierLabel} access granted`);
    }, 1050);
  };

  const handleClose = (next) => { if (!next && !unlocked) cancelTwoFactor(); };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md" data-testid="two-factor-dialog">
        <DialogHeader>
          <div className="flex justify-center mb-2">
            <LockUnlock size={64} mode={unlocked ? "once" : "locked"} data-testid="two-factor-lock" />
          </div>
          <DialogTitle
            className="text-xl font-semibold uppercase tracking-tight text-center"
            style={{ fontFamily: "Inter, sans-serif" }}
          >
            {unlocked ? "Access granted" : isEnrollment ? "Set up Token ID" : "Token ID"}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {isEnrollment ? "Scan the QR code, then enter the code to confirm." : `${tierLabel} access needed.`}
          </DialogDescription>
        </DialogHeader>

        {unlocked ? (
          <p className="text-sm text-[#6e6e73] text-center py-4">Signing you in…</p>
        ) : isEnrollment ? (
          <div className="space-y-4">
            <div className="text-sm text-[#6e6e73] leading-relaxed text-center">
              <p className="mb-1"><strong className="text-[#1d1d1f]">{tierLabel}</strong> access needed.</p>
              <p className="text-[#86868b] text-xs">First-time setup — scan the QR code below with your authenticator, then enter the code to confirm.</p>
            </div>
            <div className="flex justify-center bg-white rounded-md border border-black/[0.08] p-4 min-h-[200px] items-center">
              {loadingQr ? (
                <div className="text-sm text-[#86868b]">Generating QR code…</div>
              ) : enrollment?.qr_png_base64 ? (
                <img src={`data:image/png;base64,${enrollment.qr_png_base64}`} alt="Authenticator setup QR" className="w-44 h-44" data-testid="two-factor-qr" />
              ) : (
                <div className="text-sm text-[#c82828]">Could not load QR — please try again.</div>
              )}
            </div>
            <form onSubmit={handleSubmit} className="space-y-3">
              <Input
                data-testid="two-factor-code-input"
                type="text" inputMode="numeric" pattern="[0-9]*" maxLength={6}
                value={code} onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, ""))}
                placeholder="123 456"
                className="border-black/[0.12] rounded-lg text-center text-2xl tracking-[0.4em] font-mono"
                autoFocus
              />
              <Button
                type="submit" disabled={busy || code.length !== 6}
                data-testid="two-factor-confirm-btn"
                className="w-full bg-[#0071e3] hover:bg-[#0060c0] text-white rounded-full font-medium"
                style={{ fontFamily: "Inter, sans-serif" }}
              >
                {busy ? "Confirming…" : "Confirm & log in"}
              </Button>
            </form>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-[#6e6e73] text-center leading-relaxed">
              <strong className="text-[#1d1d1f]">{tierLabel}</strong> access needed.
            </p>
            <form onSubmit={handleSubmit} className="space-y-3">
              <Input
                data-testid="two-factor-code-input"
                type="text" inputMode="numeric" pattern="[0-9]*" maxLength={6}
                value={code} onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, ""))}
                placeholder="123 456"
                className="border-black/[0.12] rounded-lg text-center text-2xl tracking-[0.4em] font-mono"
                autoFocus
              />
              <Button
                type="submit" disabled={busy || code.length !== 6}
                data-testid="two-factor-verify-btn"
                className="w-full bg-[#0071e3] hover:bg-[#0060c0] text-white rounded-full font-medium"
                style={{ fontFamily: "Inter, sans-serif" }}
              >
                {busy ? "Verifying…" : "Verify & log in"}
              </Button>
            </form>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
